import React, { useState, useEffect, useCallback } from 'react';
import { hashPassword } from '../../utils/helpers';
import { fetchWithAuth } from '../../utils/api';

function UserProfile({ user, handleLogout }) {
  const [passwords, setPasswords] = useState({ old: '', new: '', confirm: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false });
  const [twoFactorSetup, setTwoFactorSetup] = useState(null); // { secret, url }
  const [verificationCode, setVerificationCode] = useState('');
  const [defaultPage, setDefaultPage] = useState(user.default_page || 'sales');

  const handleDefaultPageChange = (e) => {
    const newPage = e.target.value;
    setDefaultPage(newPage);
    fetchWithAuth('/users/default_page', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ default_page: newPage })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      setMessage('Default page updated successfully');
      // Update local user object too
      const updatedUser = { ...user, default_page: newPage };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    })
    .catch(err => setError(err.message));
  };

  const fetch2FAStatus = useCallback(() => {
    fetchWithAuth('/2fa/status', {}, user, handleLogout)
      .then(res => res.json())
      .then(data => setTwoFactorStatus(data))
      .catch(err => console.error(err));
  }, [user, handleLogout]);

  useEffect(() => {
    fetch2FAStatus();
  }, [fetch2FAStatus]);

  const handle2FASetup = () => {
    fetchWithAuth('/2fa/setup', {}, user, handleLogout)
      .then(res => res.json())
      .then(data => setTwoFactorSetup(data))
      .catch(err => setError(err.message));
  };

  const handle2FAVerify = (e) => {
    e.preventDefault();
    fetchWithAuth('/2fa/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: verificationCode })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      setTwoFactorSetup(null);
      setVerificationCode('');
      fetch2FAStatus();
      setMessage("2FA enabled successfully!");
    })
    .catch(err => setError(err.message));
  };

  const handle2FADisable = () => {
    if (!window.confirm("Are you sure you want to disable 2FA?")) return;
    fetchWithAuth('/2fa/disable', {
      method: 'POST'
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetch2FAStatus();
      setMessage("2FA disabled successfully.");
    })
    .catch(err => setError(err.message));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (passwords.new !== passwords.confirm) {
      setError("New passwords don't match");
      return;
    }

    const hashedOld = await hashPassword(passwords.old);
    const hashedNew = await hashPassword(passwords.new);

    fetchWithAuth('/users/password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ old_password: hashedOld, new_password: hashedNew })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      setMessage('Password updated successfully');
      setPasswords({ old: '', new: '', confirm: '' });
    })
    .catch(err => setError(err.message));
  };

  return (
    <div className="view-container">
      <div className="view-header">
        <h2>User Profile</h2>
      </div>

      {message && <div className="status-paid" style={{marginBottom: '1.5rem', padding: '1rem', borderRadius: '4px'}}>{message}</div>}
      {error && <div className="error-message" style={{marginBottom: '1.5rem'}}>{error}</div>}

      <div className="form-section">
        <h3>Preferences</h3>
        <div style={{ marginTop: '1rem' }}>
          <div className="form-group">
            <label>Default Landing Page</label>
            <select value={defaultPage} onChange={handleDefaultPageChange} style={{ maxWidth: '300px' }}>
              <option value="sales">Sales</option>
              <option value="harvest_agents">Harvest Agents</option>
              <option value="mortality">Mortality</option>
              <option value="employees">Employees</option>
            </select>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
              Select which page you want to see first after logging in.
            </p>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Change Password</h3>
        <form onSubmit={handleSubmit} style={{marginTop: '1.5rem'}}>
          <div className="form-group">
            <label>Old Password</label>
            <input type="password" value={passwords.old} onChange={e => setPasswords({...passwords, old: e.target.value})} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={passwords.new} onChange={e => setPasswords({...passwords, new: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input type="password" value={passwords.confirm} onChange={e => setPasswords({...passwords, confirm: e.target.value})} required />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="submit-btn">Update Password</button>
          </div>
        </form>
      </div>

      <div className="form-section" style={{marginTop: '2rem'}}>
        <h3>Security</h3>
        <div style={{marginTop: '1.5rem'}}>
          <h4 style={{marginBottom: '0.5rem'}}>Two-Factor Authentication (2FA)</h4>
          {twoFactorStatus.enabled ? (
            <div className="status-paid" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', border: '1px solid var(--border-color)'}}>
              <div>
                <p style={{margin: 0, fontWeight: 600}}>✓ 2FA is currently ENABLED</p>
                <p style={{margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 400}}>Your account is protected by an extra layer of security.</p>
              </div>
              <button onClick={handle2FADisable} className="cancel-btn">Disable 2FA</button>
            </div>
          ) : (
            <div style={{background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', border: '1px solid var(--border-color)'}}>
              <p style={{marginTop: 0}}>2FA adds an extra layer of security to your account. We recommend enabling it.</p>
              {!twoFactorSetup ? (
                <button onClick={handle2FASetup} className="action-btn">Setup 2FA</button>
              ) : (
                <div style={{marginTop: '1.5rem', background: '#fff', padding: '1.5rem', border: '1px solid var(--border-color)', borderRadius: '8px'}}>
                  <h4 style={{marginTop: 0}}>Scan QR Code</h4>
                  <p style={{fontSize: '0.85rem', color: 'var(--text-secondary)'}}>Scan this code with your authenticator app (like Google Authenticator or Authy).</p>
                  
                  <div style={{textAlign: 'center', margin: '1.5rem 0'}}>
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twoFactorSetup.url)}`} 
                      alt="2FA QR Code" 
                      style={{border: '1px solid var(--border-color)', borderRadius: '4px', padding: '1rem', background: '#fff'}}
                    />
                  </div>
                  
                  <div style={{background: '#f8f9fa', padding: '1rem', borderRadius: '4px', marginBottom: '1.5rem', border: '1px solid var(--border-color)'}}>
                    <p style={{margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)'}}>Or enter secret manually:</p>
                    <code style={{fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)'}}>{twoFactorSetup.secret}</code>
                  </div>
                  
                  <form onSubmit={handle2FAVerify}>
                    <div className="form-group">
                      <label>Enter Verification Code</label>
                      <input 
                        value={verificationCode} 
                        onChange={e => setVerificationCode(e.target.value)} 
                        placeholder="000000" 
                        required 
                        style={{maxWidth: '200px'}}
                      />
                    </div>
                    <div className="form-actions" style={{justifyContent: 'flex-start'}}>
                      <button type="submit" className="submit-btn">Verify & Enable</button>
                      <button type="button" className="secondary-btn" onClick={() => setTwoFactorSetup(null)}>Cancel</button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
